import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'
import { generatePdfSectionsData, isPdfFieldEmpty } from '../../common/helper'

const styles = StyleSheet.create({
  page: { padding: 20, fontFamily: 'Helvetica' },
  mainHeader: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  sectionHeader: {
    fontSize: 14,
    color: '#000',
    fontWeight: 'bold',
    paddingTop: 6,
    paddingBottom: 6,
  },
  sectionLabel: {
    fontSize: 11,
    color: '#222',
    fontWeight: 'normal',
  },
  sectionUnderline: {
    borderBottomWidth: 1,
    borderBottomColor: '#bbb',
    marginBottom: 16,
    marginTop: 4,
    width: '100%',
  },
  flexRow: {
    width: '100%',
  },
  masterDataRow: {
    marginBottom: 8,
  },
  underline: {
    borderBottomWidth: 1,
    borderBottomColor: '#bbb',
    marginVertical: 4,
    width: '100%',
  },
  masterDataItemRow: {
    marginBottom: 8,
  },
  tableHeader: {
    color: 'rgb(136,136,136)',
    fontSize: 10,
    fontWeight: 'bold',
    minWidth: 120,
    marginBottom: 2,
  },
  tableValue: {
    color: 'rgb(43,45,49)',
    fontSize: 10,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
  },
  col: {
    flex: 1,
    marginRight: 12,
    minWidth: 200,
  },
})

const RFI2PdfGenerator = ({ rfi3Template, rfi3Data, rfiMasterData }) => {
  const pdfSectionsData = generatePdfSectionsData(
    rfi3Template,
    rfi3Data,
    rfiMasterData,
    rfi3Data?.manufacturing_type,
  )

  function renderMasterData(value, keyPrefix = '') {
    if (Array.isArray(value)) {
      if (
        value.length > 0 &&
        typeof value[0] === 'object' &&
        value[0] !== null &&
        'label' in value[0] &&
        'value' in value[0]
      ) {
        return (
          <View wrap={false} key={keyPrefix} style={styles.masterDataRow}>
            {value.map((item, itemIdx) => (
              <View style={styles.masterDataItemRow} key={itemIdx}>
                <Text style={styles.tableHeader}>
                  {item.label?.replace(/<[^>]+>/g, '')}
                </Text>
                <Text style={styles.tableValue}>
                  {Array.isArray(item.value)
                    ? item.value.join(', ')
                    : typeof item.value === 'object' &&
                        item.value !== null &&
                        item.value.uploaded_files
                      ? '' // Skip rendering file upload objects
                      : item.value}
                </Text>
              </View>
            ))}
            <View style={styles.underline} />
          </View>
        )
      }
      return value.map((item, idx) =>
        renderMasterData(item, keyPrefix + idx + '-'),
      )
    } else if (
      value &&
      typeof value === 'object' &&
      value.label !== undefined &&
      value.value !== undefined
    ) {
      if (
        typeof value.value === 'object' &&
        value.value !== null &&
        value.value.uploaded_files
      ) {
        return null
      }
      return (
        <View style={styles.masterDataItemRow} key={keyPrefix}>
          <Text style={styles.tableHeader}>
            {value.label?.replace(/<[^>]+>/g, '')}
          </Text>
          <Text style={styles.tableValue}>
            {Array.isArray(value.value) ? value.value.join(', ') : value.value}
          </Text>
        </View>
      )
    } else if (value && typeof value === 'object') {
      if ('uploaded_files' in value) {
        return null
      }
      return Object.entries(value).map(([k, v], idx) =>
        renderMasterData(v, keyPrefix + k + '-'),
      )
    }
    return null
  }

  return (
    <Document>
      {pdfSectionsData.map((tab) =>
        tab.data?.map((section, sectionIdx) => {
          const masterData = section.rfi3Data?.masterData
          const filteredMasterData = Object.fromEntries(
            Object.entries(masterData || {}).filter(
              ([, value]) => !isPdfFieldEmpty(value),
            ),
          )
          const hasMasterData =
            filteredMasterData &&
            typeof filteredMasterData === 'object' &&
            Object.keys(filteredMasterData).length > 0

          if (!hasMasterData) return null

          return (
            <Page size="A4" key={section.sectionId} style={styles.page}>
              {sectionIdx === 0 && rfi3Data?.supplier_name && (
                <View
                  style={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    marginBottom: 12,
                  }}
                >
                  <View style={{ marginBottom: 4, flexDirection: 'row' }}>
                    <Text
                      style={{
                        fontSize: 12,
                        fontWeight: 'bold',
                        marginRight: 4,
                      }}
                    >
                      Supplier Name:
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      {rfi3Data.supplier_name}
                    </Text>
                  </View>
                  <View style={{ marginBottom: 4, flexDirection: 'row' }}>
                    <Text
                      style={{
                        fontSize: 12,
                        fontWeight: 'bold',
                        marginRight: 4,
                      }}
                    >
                      Supplier ID:
                    </Text>
                    <Text style={{ fontSize: 12 }}>{rfi3Data.supplier_id}</Text>
                  </View>
                  <View style={{ marginBottom: 4, flexDirection: 'row' }}>
                    <Text
                      style={{
                        fontSize: 12,
                        fontWeight: 'bold',
                        marginRight: 4,
                      }}
                    >
                      Location Name:
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      {rfi3Data.location_name}
                    </Text>
                  </View>
                  <View style={{ flexDirection: 'row' }}>
                    <Text
                      style={{
                        fontSize: 12,
                        fontWeight: 'bold',
                        marginRight: 4,
                      }}
                    >
                      Location ID:
                    </Text>
                    <Text style={{ fontSize: 12 }}>{rfi3Data.location_id}</Text>
                  </View>
                </View>
              )}
              {sectionIdx === 0 && (
                <Text style={styles.mainHeader}>{tab.parentName}</Text>
              )}
              <Text style={styles.sectionHeader}>{section.sectionName}</Text>
              {section.sectionLabel && (
                <Text style={styles.sectionLabel}>{section.sectionLabel}</Text>
              )}
              <View style={styles.sectionUnderline} />
              <View style={styles.flexRow}>
                {Object.entries(masterData).map(([key, value], idx) => {
                  if (
                    value &&
                    typeof value === 'object' &&
                    value.__sectionLabel
                  ) {
                    return (
                      <View key={key + '-section'}>
                        <Text style={styles.sectionHeader}>
                          {value.__sectionLabel}
                        </Text>
                        {Object.entries(value)
                          .filter(([k]) => !k.startsWith('__'))
                          .map(([subKey, subVal], subIdx) => {
                            if (Array.isArray(subVal)) {
                              return subVal.map((row, rowIdx) => (
                                <View key={subKey + '-' + rowIdx}>
                                  {row?.__subSectionLabel && (
                                    <Text style={styles.sectionLabel}>
                                      {row.__subSectionLabel}{' '}
                                      {row.__rowIndex ? row.__rowIndex : ''}
                                    </Text>
                                  )}
                                  {renderMasterData(
                                    row,
                                    subKey + '-' + rowIdx + '-',
                                  )}
                                </View>
                              ))
                            }
                            return null
                          })}
                      </View>
                    )
                  }
                  return renderMasterData(value, key + '-')
                })}
              </View>
            </Page>
          )
        }),
      )}
    </Document>
  )
}

export default RFI2PdfGenerator
